import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import {
  InboxRounded,
  EngineeringRounded,
  PauseCircleOutlineRounded,
  CheckCircleOutlineRounded,
  RefreshOutlined,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";

import { supporterService } from "../services/supporterService";
import OrderCard from "../components/OrderCard";
import { NoteDialog, CancelDialog } from "../components/SupporterDialogs";
import { ServerFilter, DateRangeFilter } from "../components/KanbanFilters";
import { parseServerDate } from "../../../utils/dateUtils";
import {
  KANBAN_COLUMNS,
  ORDER_STATUS,
  STORAGE_KEYS,
  POLLING_INTERVAL_MS,
  MESSAGES,
} from "../../../constants/consts";
import { playIfBackground } from "../../../utils/playNotificationSound";

/* ─────────────── Animasyonlar ─────────────── */
const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─────────────── İkon haritası ─────────────── */
const ICONS = {
  inbox: InboxRounded,
  engineering: EngineeringRounded,
  pause: PauseCircleOutlineRounded,
  check: CheckCircleOutlineRounded,
};

/* ─────────────── Sabitler ─────────────── */
const COLUMN_FILTER_KEY = {
  pool: "pool",
  progress: "progress",
  pending: "pending",
  completed: "completed",
};

const FILTER_KEY_TO_STATUS = {
  pool: ORDER_STATUS.POOL,
  progress: ORDER_STATUS.IN_PROGRESS,
  pending: ORDER_STATUS.PENDING,
  completed: ORDER_STATUS.COMPLETED,
};

const INITIAL_FILTERS = {
  pool: { sunucu: "Tümü", start: null, end: null, active: false },
  progress: { sunucu: "Tümü", start: null, end: null, active: false },
  pending: { sunucu: "Tümü", start: null, end: null, active: false },
  completed: { sunucu: "Tümü", start: null, end: null, active: false },
};

const DRAG_THRESHOLD_PX = 6; // Bu kadar hareket etmeden sürükleme başlamaz

export default function SupporterKanban({ user }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  /* ─── Drag state ─── */
  const [dragging, setDragging] = useState(null);
  const [hoveredCol, setHoveredCol] = useState(null);

  const [noteDialog, setNoteDialog] = useState({
    open: false,
    order: null,
    toStatus: null,
  });
  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    order: null,
  });
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [animKey, setAnimKey] = useState(0);

  const pollRef = useRef(null);
  const isFetchingRef = useRef(false);

  /* ─── Drag refs ─── */
  const dragStartRef = useRef(null);
  const columnRefs = useRef({});
  const suppressClickRef = useRef(false);

  const username =
    user?.username ||
    localStorage.getItem(STORAGE_KEYS.USERNAME) ||
    sessionStorage.getItem(STORAGE_KEYS.USERNAME) ||
    "";
  const userId = user?.user_id;

  /* ─────────────── Veri yükleme ─────────────── */
  const loadOrders = useCallback(
    async (silent = false) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;

      if (silent) setRefreshing(true);
      else setLoading(true);
      setError("");

      try {
        const data = await supporterService.getSupportsBySupporter({
          supporter: username,
        });

        const userIds = [...new Set(data.map((d) => String(d.user_id)))];
        const cache = {};
        for (let i = 0; i < userIds.length; i += 50) {
          const chunk = userIds.slice(i, i + 50);
          const results = await Promise.all(
            chunk.map((id) =>
              supporterService.getUserById(id).catch(() => ({})),
            ),
          );
          chunk.forEach((id, j) => (cache[id] = results[j]));
        }

        const mapped = data
          .map((item) => {
            const uid = String(item.user_id);
            const u = cache[uid] || {};
            const rawDate = parseDate(item.created_at);
            const rawEndDate = item.end_at ? parseDate(item.end_at) : null;
            return {
              id: item.id,
              supportId: item.id,
              title: item.title ?? "-",
              status: item.status ?? "",
              supporter: item.supporter ?? "",
              createdAt: formatDate(rawDate),
              createdAtDate: rawDate,
              endAt: rawEndDate,
              contract:
                item.contract === "no"
                  ? "Yok"
                  : item.contract === "yes"
                    ? "Var"
                    : item.contract === "New"
                      ? "Yeni"
                      : "",
              unread: parseInt(item.unread_nortek, 10) || 0,
              userId: uid,
              username: u.username ?? "",
              sunucuKodu: u.sunucu_kodu ?? "",
              note: item.note ?? "",
            };
          })
          .filter((o) => o.id);

        setOrders(mapped);
        setAnimKey((k) => k + 1);

        await Promise.all([
          supporterService
            .changeUpdateValue({
              target: "nortek",
              newValue: false,
              userId,
              root: "support",
            })
            .catch(() => {}),
          supporterService
            .changeUpdateValue({
              target: "nortek",
              newValue: false,
              userId,
              root: "msg",
            })
            .catch(() => {}),
        ]);
      } catch (err) {
        console.error(err);
        if (!silent) setError(MESSAGES.SUPPORT_LOAD_ERROR);
      } finally {
        if (silent) setRefreshing(false);
        else setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [username, userId],
  );

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  /* ─────────────── Polling ─────────────── */
  useEffect(() => {
    if (!userId) return;

    const tick = async () => {
      try {
        const [supportChanged, msgChanged] = await Promise.all([
          supporterService
            .checkDataIsUpdated({ userId, root: "support" })
            .catch(() => false),
          supporterService
            .checkDataIsUpdated({ userId, root: "msg" })
            .catch(() => false),
        ]);

        if (supportChanged) playIfBackground("support");
        if (msgChanged) playIfBackground("message");

        if (supportChanged || msgChanged) {
          await loadOrders(true);
        }
      } catch (err) {
        console.warn("Polling error:", err?.message);
      }
    };

    pollRef.current = setInterval(tick, POLLING_INTERVAL_MS);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, [userId, loadOrders]);

  /* ─────────────── Pointer Drag Sistemi ─────────────── */
  const handleCardPointerDown = (e, order) => {
    // Sol tık / tek parmak dokunuş
    if (e.button !== undefined && e.button !== 0) return;

    // Cancel butonu gibi engelleyiciler
    if (e.target.closest("[data-no-drag]")) return;

    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    dragStartRef.current = {
      order,
      startX: e.clientX,
      startY: e.clientY,
      offsetX: e.clientX - rect.left,
      offsetY: e.clientY - rect.top,
      width: rect.width,
      active: false,
    };
  };

  // Global pointer move/up dinleyicileri
  useEffect(() => {
    const onMove = (e) => {
      const ds = dragStartRef.current;
      if (!ds) return;

      if (!ds.active) {
        const dist = Math.hypot(e.clientX - ds.startX, e.clientY - ds.startY);
        if (dist < DRAG_THRESHOLD_PX) return;

        // Eşiği geçtik → gerçek sürükleme başlıyor
        ds.active = true;
        document.body.classList.add("is-dragging");
        document.body.style.cursor = "grabbing";

        // Hayalet kopyayı dondur (state'ten çıkarma)
        setDragging({
          order: ds.order,
          offsetX: ds.offsetX,
          offsetY: ds.offsetY,
          width: ds.width,
          x: e.clientX,
          y: e.clientY,
        });
      } else {
        setDragging((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
      }

      // Hangi kolonun üstündeyiz?
      let found = null;
      Object.entries(columnRefs.current).forEach(([status, el]) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        if (
          e.clientX >= r.left &&
          e.clientX <= r.right &&
          e.clientY >= r.top &&
          e.clientY <= r.bottom
        ) {
          found = status;
        }
      });
      setHoveredCol(found);
    };

    const onUp = async () => {
      const ds = dragStartRef.current;
      dragStartRef.current = null;
      document.body.classList.remove("is-dragging");
      document.body.style.cursor = "";

      if (!ds) return;

      // Eğer sürükleme başlamadıysa → normal tıklama, dokunma
      if (!ds.active) return;

      // Sürükleme bitti → click event'ini bastır
      suppressClickRef.current = true;
      setTimeout(() => {
        suppressClickRef.current = false;
      }, 100);

      const d = dragging;
      const target = hoveredCol;
      setDragging(null);
      setHoveredCol(null);

      if (!d || !target) return;
      if (target === d.order.status) return;

      const needsNote =
        target === ORDER_STATUS.PENDING || target === ORDER_STATUS.COMPLETED;

      if (needsNote) {
        setNoteDialog({
          open: true,
          order: d.order,
          toStatus: target,
        });
        return;
      }

      await moveOrder(d.order, d.order.status, target, "");
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);

    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
    // eslint-disable-next-line
  }, [dragging, hoveredCol]);

  /* ─────────────── Kolona özel sunucu seçenekleri ─────────────── */
  const sunucuOptionsByStatus = useMemo(() => {
    const map = {};
    KANBAN_COLUMNS.forEach((col) => {
      const set = new Set();
      orders.forEach((o) => {
        if (o.status === col.status && o.sunucuKodu) {
          set.add(o.sunucuKodu);
        }
      });
      const sorted = Array.from(set).sort((a, b) => a.localeCompare(b, "tr"));
      map[col.status] = ["Tümü", ...sorted];
    });
    return map;
  }, [orders]);

  /* ─────────────── Seçili sunucu düşerse sıfırla ─────────────── */
  useEffect(() => {
    setFilters((prev) => {
      let changed = false;
      const next = { ...prev };
      Object.entries(FILTER_KEY_TO_STATUS).forEach(([key, status]) => {
        const opts = sunucuOptionsByStatus[status] || [];
        const current = next[key]?.sunucu;
        if (current && current !== "Tümü" && !opts.includes(current)) {
          next[key] = { ...next[key], sunucu: "Tümü" };
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sunucuOptionsByStatus]);

  /* ─────────────── Filtreleme ─────────────── */
  const applyFilters = useCallback((list, f) => {
    let out = list;
    if (f?.sunucu && f.sunucu !== "Tümü") {
      out = out.filter((o) => o.sunucuKodu === f.sunucu);
    }
    if (f?.active && f.start && f.end) {
      const s = startOfDay(new Date(f.start));
      const e = endOfDay(new Date(f.end));
      out = out.filter((o) => {
        if (!o.createdAtDate) return false;
        return o.createdAtDate >= s && o.createdAtDate <= e;
      });
    }
    return out;
  }, []);

  /* ─────────────── Kolon grupları ─────────────── */
  // const columns = useMemo(() => {
  //   const map = {};
  //   KANBAN_COLUMNS.forEach((col) => {
  //     map[col.status] = orders.filter((o) => o.status === col.status);
  //   });

  //   map[ORDER_STATUS.IN_PROGRESS] = (map[ORDER_STATUS.IN_PROGRESS] || []).sort(
  //     (a, b) => b.unread - a.unread,
  //   );

  //   map[ORDER_STATUS.POOL] = applyFilters(
  //     map[ORDER_STATUS.POOL] || [],
  //     filters.pool,
  //   );
  //   map[ORDER_STATUS.IN_PROGRESS] = applyFilters(
  //     map[ORDER_STATUS.IN_PROGRESS] || [],
  //     filters.progress,
  //   );
  //   map[ORDER_STATUS.PENDING] = applyFilters(
  //     map[ORDER_STATUS.PENDING] || [],
  //     filters.pending,
  //   );
  //   map[ORDER_STATUS.COMPLETED] = applyFilters(
  //     map[ORDER_STATUS.COMPLETED] || [],
  //     filters.completed,
  //   );

  //   return map;
  // }, [orders, filters, applyFilters]);
  const columns = useMemo(() => {
    const map = {};
    KANBAN_COLUMNS.forEach((col) => {
      map[col.status] = orders.filter((o) => o.status === col.status);
    });

    map[ORDER_STATUS.IN_PROGRESS] = (map[ORDER_STATUS.IN_PROGRESS] || []).sort(
      (a, b) => b.unread - a.unread,
    );

    map[ORDER_STATUS.POOL] = applyFilters(
      map[ORDER_STATUS.POOL] || [],
      filters.pool,
    );
    map[ORDER_STATUS.IN_PROGRESS] = applyFilters(
      map[ORDER_STATUS.IN_PROGRESS] || [],
      filters.progress,
    );
    map[ORDER_STATUS.PENDING] = applyFilters(
      map[ORDER_STATUS.PENDING] || [],
      filters.pending,
    );

    // ─── TAMAMLANAN: sadece bugün + sunucu filtresi (tarih filtresi yok) ───
    const todayStart = startOfDay(new Date());
    const todayEnd = endOfDay(new Date());

    const completedToday = (map[ORDER_STATUS.COMPLETED] || []).filter((o) => {
      const d = o.endAt || o.createdAtDate; // end_at varsa onu kullan
      if (!d) return false;
      return d >= todayStart && d <= todayEnd;
    });

    // Sadece sunucu filtresi uygula — tarih filtresi devre dışı
    map[ORDER_STATUS.COMPLETED] = applyFilters(completedToday, {
      ...filters.completed,
      active: false, // ← tarih filtresi iptal
    });

    return map;
  }, [orders, filters, applyFilters]);
  /* ─────────────── Filtre güncelleme ─────────────── */
  const updateFilter = (key, patch) =>
    setFilters((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));

  // const isFiltered = (key) => {
  //   const f = filters[key];
  //   if (!f) return false;
  //   if (f.sunucu && f.sunucu !== "Tümü") return true;
  //   if (f.active) return true;
  //   return false;
  // };
  const isFiltered = (key) => {
    const f = filters[key];
    if (!f) return false;
    if (f.sunucu && f.sunucu !== "Tümü") return true;
    // Tamamlanan sütununda tarih filtresi yok — sadece sunucu
    if (key !== "completed" && f.active) return true;
    return false;
  };
  /* ─────────────── Status değiştirme ─────────────── */
  const moveOrder = async (order, fromStatus, toStatus, note) => {
    setOrders((prev) =>
      prev.map((o) => (o.id === order.id ? { ...o, status: toStatus } : o)),
    );
    try {
      await supporterService.updateSupportStatus({
        supportId: order.supportId,
        status: toStatus,
        supporter: username,
        note: note || "",
        statusFrom: fromStatus,
      });
      await supporterService.changeUpdateValue({
        target: "user",
        newValue: true,
        userId: order.userId,
        root: "support",
      });
    } catch (e) {
      console.error(e);
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, status: fromStatus } : o)),
      );
    }
  };

  const handleNoteConfirm = async (note) => {
    const { order, toStatus } = noteDialog;
    setNoteDialog({ open: false, order: null, toStatus: null });
    if (!order || !toStatus) return;
    await moveOrder(order, order.status, toStatus, note);
  };

  const handleCancelConfirm = async (note) => {
    const { order } = cancelDialog;
    setCancelDialog({ open: false, order: null });
    if (!order) return;
    try {
      await supporterService.cancelSupport({
        supportId: order.supportId,
        note,
      });
      await loadOrders(true);
      await supporterService.changeUpdateValue({
        target: "user",
        newValue: true,
        userId: order.userId,
        root: "support",
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleOpenChat = (order) => {
    // Sürükleme sonrası tetiklenen click'i bastır
    if (suppressClickRef.current) return;
    navigate(`/support/${order.id}/messages`, {
      state: {
        title: order.title,
        status: order.status,
        sunucuKodu: order.sunucuKodu,
      },
    });
  };

  /* ─────────────── Render ─────────────── */
  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          mb: 2,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} color="#fff">
            {MESSAGES.SUPPORTER_TITLE}
          </Typography>
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.75)" }}>
            {MESSAGES.SUPPORTER_SUBTITLE}
          </Typography>
        </Box>
        <IconButton
          onClick={() => loadOrders(true)}
          disabled={loading || refreshing}
          sx={{
            color: "#fff",
            backgroundColor: "rgba(255,255,255,0.12)",
            backdropFilter: "blur(8px)",
            transition: "transform 0.2s ease, background 0.2s ease",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.22)",
              transform: "scale(1.08)",
            },
            "&:active": { transform: "scale(0.92)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.5)" },
          }}
        >
          <RefreshOutlined
            sx={{
              animation:
                loading || refreshing ? "spin 1.2s linear infinite" : "none",
              "@keyframes spin": {
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
        </IconButton>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2, flexShrink: 0 }}>
          {error}
        </Alert>
      )}

      {loading && orders.length === 0 ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#fff" }} />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            position: "relative",
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
            gap: 1.5,
            overflow: "hidden",
          }}
        >
          {KANBAN_COLUMNS.map((col, idx) => {
            const Icon = ICONS[col.icon];
            const items = columns[col.status] || [];
            const isOver = hoveredCol === col.status && !!dragging;
            const filterKey = COLUMN_FILTER_KEY[col.key];
            const hasFilter = filterKey ? isFiltered(filterKey) : false;
            const isSourceCol = dragging?.order?.status === col.status;

            return (
              <Box
                key={col.key}
                ref={(el) => (columnRefs.current[col.status] = el)}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  minHeight: 0,
                  height: "100%",
                  borderRadius: 2.5,
                  overflow: "hidden",
                  animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) ${
                    0.15 + idx * 0.08
                  }s backwards`,
                  transition:
                    "transform 0.25s ease, box-shadow 0.25s ease, outline 0.2s ease",
                  transform: isOver ? "scale(1.02)" : "none",
                  outline: isOver
                    ? `3px dashed ${col.accent}`
                    : "3px solid transparent",
                  outlineOffset: 4,
                  boxShadow: isOver
                    ? `0 14px 34px ${col.accent}66`
                    : "0 8px 22px rgba(0,0,0,0.18)",
                  opacity: dragging && !isOver && !isSourceCol ? 0.7 : 1,
                }}
              >
                {/* Kolon başlığı */}
                <Box
                  sx={{
                    flexShrink: 0,
                    backgroundColor: col.accent,
                    color: "#fff",
                    px: 1.5,
                    py: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                  <Typography sx={{ fontWeight: 800, fontSize: 13, flex: 1 }}>
                    {col.title}
                  </Typography>

                  {hasFilter && (
                    <Box
                      title="Filtre aktif"
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        backgroundColor: "#fff",
                        boxShadow: "0 0 0 3px rgba(255,255,255,0.28)",
                        animation: "filterPulse 1.6s ease-in-out infinite",
                        "@keyframes filterPulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.45 },
                        },
                      }}
                    />
                  )}

                  <Box
                    sx={{
                      minWidth: 24,
                      height: 20,
                      px: 0.75,
                      borderRadius: 10,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(255,255,255,0.25)",
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    {items.length}
                  </Box>
                </Box>

                {/* Filtreler */}
                {/* {filterKey && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 0.75,
                      pt: 0.75,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ServerFilter
                      value={filters[filterKey].sunucu}
                      options={sunucuOptionsByStatus[col.status] || ["Tümü"]}
                      onChange={(v) => updateFilter(filterKey, { sunucu: v })}
                      accent={col.accent}
                    />

                    <DateRangeFilter
                      start={filters[filterKey].start}
                      end={filters[filterKey].end}
                      active={!!filters[filterKey].active}
                      onStartChange={(v) =>
                        updateFilter(filterKey, { start: v, active: false })
                      }
                      onEndChange={(v) =>
                        updateFilter(filterKey, { end: v, active: false })
                      }
                      onApply={() => updateFilter(filterKey, { active: true })}
                      onClear={() =>
                        updateFilter(filterKey, {
                          start: null,
                          end: null,
                          active: false,
                        })
                      }
                      accent={col.accent}
                    />

                    <Box sx={{ height: 6 }} />
                  </Box>
                )} */}
                {filterKey && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      px: 0.75,
                      pt: 0.75,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      borderBottom: "1px solid rgba(255,255,255,0.08)",
                    }}
                  >
                    <ServerFilter
                      value={filters[filterKey].sunucu}
                      options={sunucuOptionsByStatus[col.status] || ["Tümü"]}
                      onChange={(v) => updateFilter(filterKey, { sunucu: v })}
                      accent={col.accent}
                    />

                    {/* Tarih filtresi: Tamamlanan sütununda gösterilmez */}
                    {col.key !== "completed" && (
                      <DateRangeFilter
                        start={filters[filterKey].start}
                        end={filters[filterKey].end}
                        active={!!filters[filterKey].active}
                        onStartChange={(v) =>
                          updateFilter(filterKey, { start: v, active: false })
                        }
                        onEndChange={(v) =>
                          updateFilter(filterKey, { end: v, active: false })
                        }
                        onApply={() =>
                          updateFilter(filterKey, { active: true })
                        }
                        onClear={() =>
                          updateFilter(filterKey, {
                            start: null,
                            end: null,
                            active: false,
                          })
                        }
                        accent={col.accent}
                      />
                    )}

                    <Box sx={{ height: 6 }} />
                  </Box>
                )}
                {/* Kart listesi */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    overflowX: "hidden",
                    p: 1,
                    backgroundColor: isOver
                      ? `${col.accent}22`
                      : "rgba(255,255,255,0.06)",
                    backdropFilter: "blur(4px)",
                    transition: "background-color 0.2s ease",
                    "&::-webkit-scrollbar": { width: 6 },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(255,255,255,0.25)",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "rgba(255,255,255,0.4)",
                    },
                    "&::-webkit-scrollbar-track": { background: "transparent" },
                  }}
                >
                  {items.length === 0 ? (
                    <Box
                      sx={{
                        py: 4,
                        textAlign: "center",
                        color: "rgba(255,255,255,0.55)",
                        fontSize: 12,
                      }}
                    >
                      <Icon sx={{ fontSize: 30, opacity: 0.4, mb: 0.5 }} />
                      <div>
                        {filterKey && isFiltered(filterKey)
                          ? "Filtreye uygun talep yok"
                          : "Talep yok"}
                      </div>
                    </Box>
                  ) : (
                    items.map((o, i) => (
                      <Box
                        key={`${o.id}-${animKey}`}
                        sx={{
                          opacity: dragging?.order?.id === o.id ? 0.35 : 1,
                          transition: "opacity 0.2s ease",
                          filter:
                            dragging?.order?.id === o.id
                              ? "grayscale(0.5)"
                              : "none",
                        }}
                      >
                        <OrderCard
                          order={o}
                          index={i}
                          onPointerDown={handleCardPointerDown}
                          onOpenChat={() => handleOpenChat(o)}
                          onCancel={() =>
                            setCancelDialog({ open: true, order: o })
                          }
                        />
                      </Box>
                    ))
                  )}
                </Box>
              </Box>
            );
          })}

          {/* Refresh overlay */}
          {refreshing && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                backgroundColor: "rgba(10,20,35,0.45)",
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                borderRadius: 2.5,
                animation: "overlayIn 0.2s ease-out both",
                "@keyframes overlayIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              <CircularProgress
                size={42}
                thickness={4}
                sx={{
                  color: "#fff",
                  filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.35))",
                }}
              />
              <Typography
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 13,
                  letterSpacing: "0.03em",
                  textShadow: "0 2px 6px rgba(0,0,0,0.4)",
                  animation: "textPulse 1.4s ease-in-out infinite",
                  "@keyframes textPulse": {
                    "0%, 100%": { opacity: 0.65 },
                    "50%": { opacity: 1 },
                  },
                }}
              >
                Güncelleniyor…
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* ─────────── DRAG GHOST ─────────── */}
      {dragging && (
        <Box
          sx={{
            position: "fixed",
            left: dragging.x - dragging.offsetX,
            top: dragging.y - dragging.offsetY,
            width: dragging.width,
            zIndex: 9999,
            pointerEvents: "none",
            transform: "rotate(2deg) scale(1.03)",
            opacity: 0.95,
            filter: "drop-shadow(0 20px 40px rgba(0,0,0,0.40))",
            willChange: "left, top",
          }}
        >
          <OrderCard order={dragging.order} index={0} ghostMode />
        </Box>
      )}

      {/* Dialoglar */}
      <NoteDialog
        open={noteDialog.open}
        toStatus={noteDialog.toStatus}
        onClose={() =>
          setNoteDialog({ open: false, order: null, toStatus: null })
        }
        onConfirm={handleNoteConfirm}
      />
      <CancelDialog
        open={cancelDialog.open}
        order={cancelDialog.order}
        onClose={() => setCancelDialog({ open: false, order: null })}
        onConfirm={handleCancelConfirm}
      />
    </Box>
  );
}

/* ─────────────── Tarih yardımcıları ─────────────── */
// function parseDate(input) {
//   if (!input) return null;
//   try {
//     const d = new Date(input);
//     if (!isNaN(d.getTime())) return d;
//   } catch {}
//   try {
//     const cleaned = String(input).replace(/^[A-Za-z]{3},\s*/, "");
//     const d = new Date(cleaned);
//     if (!isNaN(d.getTime())) return d;
//   } catch {}
//   return null;
// }

// function formatDate(d) {
//   if (!d) return "";
//   const p = (n) => String(n).padStart(2, "0");
//   return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(
//     d.getHours(),
//   )}:${p(d.getMinutes())}`;
// }

// function startOfDay(d) {
//   return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
// }

// function endOfDay(d) {
//   return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
// }
/* ─────────────── Tarih yardımcıları ─────────────── */
function parseDate(input) {
  return parseServerDate(input);
}

function formatDate(d) {
  if (!d || isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} - ${p(
    d.getHours(),
  )}:${p(d.getMinutes())}`;
}

function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function endOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
